import { useId } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import SecretInput from '@/components/atoms/SecretInput';
import { Switch } from '@/components/atoms/Switch';
import ModalComponent from '@/components/molecules/ModalComponent/ModalComponent';
import type { ProviderAccount } from '@/domain/providerConfig';
import { toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import {
    errorKeyOf,
    fieldLabelSuffix,
    ProviderSchemaField,
} from '@/routes/shared/ProviderConfig/ProviderSchemaField';
import { StoredExtrasList } from '@/routes/shared/ProviderConfig/StoredExtrasList';
import { secretFieldsOf, unreadableAt } from '@/routes/shared/ProviderConfig/schemaFields';

import { accountSchemaOf, accountValueFields } from './accountForm';
import { accountLabel } from './accountLabel';
import { useAccountEditor } from './useAccountEditor';

import styles from './DataShares.module.scss';

export interface EditAccountModalProps {
    account: ProviderAccount;
    isOpen: boolean;
    onClose: () => void;
}

/** One account row's settings and secrets; without a row yet, saving creates it. */
function EditAccountModal({ account, isOpen, onClose }: EditAccountModalProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const { form, base, isSaving, onSubmit, saveDisabled, formMessageKey, formRef } =
        useAccountEditor(account, onClose);
    const providerLabel = accountLabel(t, base);
    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={onClose}
            // Typed secrets are lost on an accidental backdrop click otherwise.
            backdrop="static"
            title={t('providerConfig.accounts.editTitle', { provider: providerLabel })}
            body={
                <form
                    ref={formRef}
                    className={styles.form}
                    noValidate
                    onSubmit={(event) => {
                        event.preventDefault();
                        void onSubmit();
                    }}>
                    <Controller
                        control={form.control}
                        name="isActive"
                        render={({ field }) => (
                            <Switch
                                id={`${id}-active`}
                                label={t('providerConfig.accounts.active')}
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                    {accountValueFields(base).map((field) => (
                        <ProviderSchemaField
                            key={field.key}
                            field={field}
                            control={form.control}
                            name={`fields.${toFormKey(field.key)}`}
                            disabled={isSaving}
                            unreadable={unreadableAt(base, field.key)}
                        />
                    ))}
                    {secretFieldsOf(accountSchemaOf(base)).map(({ name, labelKey, required }) => (
                        <Controller
                            key={name}
                            control={form.control}
                            name={`secrets.${name}`}
                            render={({ field, fieldState }) => (
                                <FormField
                                    label={t(labelKey)}
                                    // Kept free of "password", which password managers fill.
                                    htmlFor={`${id}-account-${name}`}
                                    labelSuffix={fieldLabelSuffix({ optional: !required }, t)}
                                    error={
                                        fieldState.error
                                            ? t(errorKeyOf(fieldState.error.message))
                                            : null
                                    }>
                                    <SecretInput
                                        required={required}
                                        status={base.credentials[name] === true ? 'set' : 'notSet'}
                                        changedAt={null}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={isSaving}
                                    />
                                </FormField>
                            )}
                        />
                    ))}
                    {base.storedExtras.length > 0 && (
                        <div className={styles.storedExtras}>
                            <h3 className={styles.subheading}>
                                {t('providerConfig.sections.unknownExtra')}
                            </h3>
                            <StoredExtrasList entries={base.storedExtras} />
                        </div>
                    )}
                    {/* Mounted while empty, so the message is read out when it arrives. */}
                    <div aria-live="assertive">
                        {formMessageKey !== null && (
                            <Alert tone="danger" size="small" live="off">
                                {t(formMessageKey)}
                            </Alert>
                        )}
                    </div>
                </form>
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>
                        {t('providerConfig.accounts.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => void onSubmit()}
                        disabled={saveDisabled}>
                        {t('providerConfig.accounts.save')}
                    </Button>
                </>
            }
        />
    );
}

export default EditAccountModal;
export { EditAccountModal };
